<template>
  <Line
    v-if="line"
    :data="data"
    :options="options"
    :plugins="plugins"
  />
  <Bar
    v-else
    :data="data"
    :options="options"
    :plugins="plugins"
  />
    <!-- :options="{responsive: true, maintainAspectRatio: false}" -->
</template>

<script>
import Chart from 'chart.js/auto'
// import ChartDataLabels from 'chartjs-plugin-datalabels'
import ChartDataAnnotation from 'chartjs-plugin-annotation'
import { Bar, Line } from 'vue-chartjs'
import '@/helpers/time'

Chart.register(ChartDataAnnotation)
Chart.defaults.color = 'rgb(75 85 99)'
// Chart.defaults.font.family = 'Lexend'

export default {
  name: 'TimeseriesGraph',
  components: {
    Bar,
    Line,
  },
  props: {
    dataset: Array, // Array of the data, in the format:
    // { date: <Date Object>, datapoint: X }
    targetLevels: Array,
    targetRanges: Array,
    stepSize: Number,
    formatter: Function,
    line: Boolean,
    startAtZero: Boolean,
    dateFormat: Object,
  },
  emits: [
    'on-hover',
  ],
  data() {
    const today = new Date()
 
    return {
      // labels: [],
      currentDay: today.toLocaleDateString(undefined, {weekday: 'short'}),
      // datapoints: [],
      data: this.updateData(),
      options: this.updateOptions(),
      plugins: [
        ChartDataAnnotation
      ],
    }
  },
  beforeMount() {
    this.updateData()
    this.updateOptions()
  },
  watch: {
    dataset() {
      this.updateData()
      this.updateOptions()
    },
    // TODO: Fix target level line rendering a second delayed.
    targetLevels() {
      this.updateOptions()
    },
    targetRanges() {
      this.updateOptions()
    },
  },
  methods: {
    updateData() {
      // TODO:
      // (1) Ensure the dataset is sorted by date ascending first before splitting out.
      // (2) Figure out how to pad the days (most likely from the component caller).
      // this.labels = this.dataset.map((x) => x?.date.day())
      // this.labels = this.dataset?.map((x) => x?.date)
      // this.datapoints = this.dataset?.map((x) => x?.datapoint)
      // TODO: Might have to do this for line chart.
      // this.datapoint = this.datapoints?.map((x) => x === null ? Number.NaN : x)
      this.data = {
        // TODO: Remove date formatting to `@/helpers/time`.
        labels: this.labels?.map((date) => {
          if (date instanceof Date) {
            return this.dateFormat ? date?.toLocaleString('default', this.dateFormat) : date?.toLocaleString('default', { month: 'short', day: 'numeric' })
          }
        }),
        datasets: [
          {
            data: this.datapoints,
            backgroundColor: '#2dd4bf',
            borderColor: '#2dd4bf',
            borderWidth: 2,
            borderSkipped: false,
          },
        ]
      }
      return this.data
    },
    updateOptions() {
      this.options = {
        responsive: true,
        maintainAspectRatio: false,
        borderRadius: 50,
        datasets: {
          bar: {
            barThickness: this.datapoints?.length < 10 ? 15 : 5,
            categoryPercentage: 1.0,
          }
        },
        scales: {
          x: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
              offset: false,
              color: 'rgb(235, 235, 235)',
            },
            border: {
              display: false,
            },
            ticks: {
              maxTicksLimit: this.datapoints?.length < 10 ? 7 : 4,
              font: {
                weight: this.labels?.map(v => v === this.currentDay ? 'bold' : undefined),
              },
            },
          },
          y: {
            display: true,
            title: {
              display: false,
            },
            position: 'right',
            ...(this.startAtZero && {beginAtZero: this.startAtZero}),
            ...(this.targetRanges && {suggestedMin: Math.min(...this.targetRanges.flat()) - this.stepSize / 2}),
            ...(this.targetRanges && {suggestedMax: Math.max(...this.targetRanges.flat()) + this.stepSize / 2}),
            ticks: {
              stepSize: this.stepSize,
              maxTicksLimit: 6,
              callback: (value) => {
                return this.formatter(value)
              },
              color: (context) => {
                if (this.targetLevels?.includes(context.tick.value)) {
                  // || this.targetRanges?.flat().includes(context.tick.value)) {
                  return 'rgb(20 184 166)'
                }
              },
            },
            grid: {
              drawOnChartArea: false,
              color: 'rgb(235, 235, 235)',
            },
            border: {
              display: false,
            },
            // Custom target tick.
            afterTickToLabelConversion: (context) => {
              this.targetLevels?.forEach(target => {
                // If the target tick already exists, don't add it.
                if (!context.ticks.some(tick => target === tick.value)) {
                  context.ticks.push({ value: target, label: this.formatter(target) })
                }
              })
              // this.targetRanges?.flat().forEach(target => {
              //   // If the target tick already exists, don't add it.
              //   if (!context.ticks.some(tick => target === tick.value)) {
              //     context.ticks.push({ value: target, label: this.formatter(target) })
              //   }
              // })
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          // Target levels.
          annotation: {
            annotations: () => {
              let result = {}
              // Target lines.
              this.targetLevels?.forEach(target => {
                result['line' + target] = {
                  type: 'line',
                  borderColor: 'rgb(235, 235, 235)',
                  borderWidth: 1,
                  scaleID: 'y',
                  drawTime: 'beforeDatasetsDraw',
                  value: target,
                }
              })
              // Target ranges.
              this.targetRanges?.forEach(target => {
                result['box' + target] = {
                  type: 'box',
                  backgroundColor: 'rgba(204, 251, 241, 0.25)',
                  borderWidth: 0,
                  yMax: Math.max(...target),
                  yMin: Math.min(...target),
                  drawTime: 'beforeDatasetsDraw',
                  borderRadius: 3,
                }
              })
              // Subtending lines for target ranges.
              this.targetRanges?.flat().forEach(target => {
                result['subtend' + target] = {
                  type: 'line',
                  borderColor: 'rgb(235, 235, 235)',
                  borderWidth: 2,
                  borderDash: [2, 4],
                  scaleID: 'y',
                  drawTime: 'beforeDatasetsDraw',
                  value: target,
                }
              })
              return result
            },
          },
          tooltip: {
            // enabled: false,
            position: 'nearest',
            interaction: {
              intersect: false,
            },
            callbacks: {
              title: (contextArray) => {
                const context = contextArray.find(Boolean)
                // TODO: Remove date formatting to `@/helpers/time`.
                return context.label + ', ' + this.labels[context.dataIndex].getDate() + ' ' + this.labels[context.dataIndex].toLocaleString('default', { month: 'short' })
              },
              label: (context) => {
                return context.parsed._custom ? [this.formatter(context.parsed._custom.min), this.formatter(context.parsed._custom.max)] : this.formatter(context.parsed.y)
              },
            },
            external: (context) => {
              // const {chart, tooltip} = context
              this.$emit('on-hover', context)
            },
          },
        },
      }
      return this.options
    },
  },
  computed: {
    labels() {
      return this.dataset?.map((x) => x?.date)
    },
    datapoints() {
      return this.dataset?.map((x) => x?.datapoint)
    },
  },
}
</script>